import type {
  ApplianceCategory as GeneratedApplianceCategory,
  ApplianceCountByProgrammingCategory as GeneratedApplianceCountByProgrammingCategory,
  HospitalitySectionForestResponse as GeneratedSectionForestResponse,
  HospitalitySectionNodeResponse as GeneratedSectionNodeResponse,
  ProgrammableApplianceView as GeneratedProgrammableApplianceView,
  SectionCategoryViews as GeneratedSectionCategoryViews,
  SectionDeletableResponse as GeneratedSectionDeletableResponse,
  SectionParentRef as GeneratedSectionParentRef,
  SiteSectionCategoryCreatedEntityReference as GeneratedSiteSectionCategoryCreatedEntityReference,
  SiteSectionCreatedEntityReference as GeneratedSiteSectionCreatedEntityReference,
} from "@/.generated/hey/v4b-backend/types.gen";
import type { SiteSectionCategoryId } from "@/data/common/ids";
import type {
  ApplianceCountByCategory,
  CreatedSiteSectionCategory,
  CreatedSiteSection,
  ProgrammableAppliance,
  SectionCategory,
  SectionCategoryReference,
  SectionDeletability,
  SectionForest,
  SectionNode,
  SectionParentReference,
} from "@/domains/v4b/site-section/site-section.types";

import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import { brandNumber } from "@/utils/branded";
import { entries } from "@/utils/object";
import { assertUnreachable } from "@/utils/types-utils";

export function mapCreatedSiteSection(response: GeneratedSiteSectionCreatedEntityReference): CreatedSiteSection {
  return {
    id: brandNumber(response.id),
    type: response.type,
  };
}

export function mapCreatedSiteSectionCategory(
  response: GeneratedSiteSectionCategoryCreatedEntityReference,
): CreatedSiteSectionCategory {
  return {
    id: brandNumber<SiteSectionCategoryId>(response.id),
    type: response.type,
  };
}

export function mapSectionCategories(response: GeneratedSectionCategoryViews): SectionCategory[] {
  return response.map((category) => ({
    id: brandNumber<SiteSectionCategoryId>(category.id),
    name: category.name,
    isDeletable: category.isDeletable,
    linkedSectionCount: category.linkedSectionCount,
  }));
}

export function mapSectionForest(response: GeneratedSectionForestResponse): SectionForest {
  return {
    sections: response.sections.map(mapSectionNode),
  };
}

function mapSectionNode(node: GeneratedSectionNodeResponse): SectionNode {
  return {
    id: brandNumber(node.id),
    name: node.name,
    area: node.area ?? undefined,
    parent: node.parent ? mapSectionParentReference(node.parent) : undefined,
    category: node.category ? mapSectionCategoryReference(node.category) : undefined,
    applianceCountByCategory: mapApplianceCountByCategory(node.applianceCountByProgrammingCategory),
    children: node.children.map(mapSectionNode),
  };
}

export function mapSectionCategoryReference(
  category: NonNullable<GeneratedSectionNodeResponse["category"]>,
): SectionCategoryReference {
  return {
    id: brandNumber<SiteSectionCategoryId>(category.id),
    name: category.name,
  };
}

function mapSectionParentReference(parent: GeneratedSectionParentRef): SectionParentReference {
  return {
    id: brandNumber(parent.id),
    namePath: parent.namePath,
  };
}

export function mapSectionDeletability(response: GeneratedSectionDeletableResponse): SectionDeletability {
  return {
    isDeletable: response.isDeletable,
  };
}

function mapApplianceCategory(category: GeneratedApplianceCategory): EProgramApplianceCategory {
  switch (category) {
    case "AC_UNIT_NON_INFRARED":
      return EProgramApplianceCategory.AC_UNIT_NON_INFRARED;
    case "AIR_HANDLING_UNIT":
      return EProgramApplianceCategory.AIR_HANDLING_UNIT;
    case "BOILER":
      return EProgramApplianceCategory.BOILER;
    case "BOOKABLE_SPACE":
      return EProgramApplianceCategory.BOOKABLE_SPACE;
    case "CHARGING_STATION":
      return EProgramApplianceCategory.CHARGING_STATION;
    case "CHILLER":
      return EProgramApplianceCategory.CHILLER;
    case "CHILLER_FOOD_GRADE":
      return EProgramApplianceCategory.CHILLER_FOOD_GRADE;
    case "GENERATOR_SET":
      return EProgramApplianceCategory.GENERATOR_SET;
    case "HEATER":
      return EProgramApplianceCategory.HEATER;
    case "HEATER_STORAGE":
      return EProgramApplianceCategory.HEATER_STORAGE;
    case "HEATER_UNIT":
      return EProgramApplianceCategory.HEATER_UNIT;
    case "HEATING_FLOOR":
      return EProgramApplianceCategory.HEATING_FLOOR;
    case "HEAT_PUMP":
      return EProgramApplianceCategory.HEAT_PUMP;
    case "HEAT_PUMP_DHW":
      return EProgramApplianceCategory.HEAT_PUMP_DHW;
    case "HEAT_PUMP_WITH_TEMPERATURE":
      return EProgramApplianceCategory.HEAT_PUMP_WITH_TEMPERATURE;
    case "POOL_HEATER":
      return EProgramApplianceCategory.POOL_HEATER;
    case "PUMP_MOTOR":
      return EProgramApplianceCategory.PUMP_MOTOR;
    case "RADIANT_CEILING_PANEL":
      return EProgramApplianceCategory.RADIANT_CEILING_PANEL;
    case "WATER_HEATER":
      return EProgramApplianceCategory.WATER_HEATER;
    default:
      return assertUnreachable(category);
  }
}

export function mapApplianceCountByCategory(
  response: GeneratedApplianceCountByProgrammingCategory,
): ApplianceCountByCategory {
  const result: ApplianceCountByCategory = {};

  for (const [category, count] of entries(response)) {
    if (count !== undefined) result[mapApplianceCategory(category)] = count;
  }

  return result;
}

export function mapProgrammableAppliances(response: GeneratedProgrammableApplianceView[]): ProgrammableAppliance[] {
  return response.map((appliance) => ({
    applianceId: brandNumber(appliance.applianceId),
    category: mapApplianceCategory(appliance.category),
    name: appliance.name,
  }));
}
